// =============================================================================
// CompanyEntities — Settings tab for OUR legal entities.
//
// THE ROSTER IS THE SAGE ENTITY LIST. It used to be its own hand-typed table
// (dbo.CompanyEntities), which nobody ever filled in — so this tab read "No
// entities yet" next to a Sage "Entities" import reporting 42 inserted, and the
// buyer/seller detection on a settlement statement matched against nothing.
// Migration 189 dropped that table; this now reads GET /api/sage/entities.
//
// So Name / entity ID / Federal ID are READ-ONLY here: Sage owns them and
// re-imports them on every emailed export, so a hand edit would be silently
// overwritten. What a person owns is the ALIASES — the spellings a title company
// puts on a settlement statement, which Sage has never heard of — and Notes.
// =============================================================================
import { useState } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Card, Badge, Modal, ModalFooter, Textarea, Tabs, useToast } from '../components/ui';
import { EntityOrgChart } from '../components/shared/EntityOrgChart';
import { EntityDocumentsPanel, splitAliasNames } from '../components/shared/EntityDocumentsPanel';

// Same rule as the API's aliasList: a comma before a bare legal form (", LLC")
// is part of the name, not a separator.
const splitAliases = splitAliasNames;

const AliasModal = ({ entity, onClose, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ aliases: entity.aliases || '', notes: entity.notes || '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // The server's own copy of the saved row comes back, so the list renders
      // what landed rather than what we hoped we sent.
      const saved = await api.put(`sage/entities/${entity.sageEntityID}`, form);
      toast.success('Saved');
      onSaved(saved); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Aliases — ${entity.name || entity.entityCode}`} onClose={onClose} width={560}>
      <div style={{ background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: B.textLt }}>Sage entity ID</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: B.text }}>{entity.entityCode}</div>
        <div style={{ fontSize: 11, color: B.textLt, marginTop: 4 }}>
          The ID, name and Federal ID come from Sage and are re-imported on every export — change them in Sage, not here.
        </div>
      </div>
      <Textarea label="Aliases (one per line)" value={form.aliases} onChange={v => set('aliases', v)} rows={4}
        placeholder={'E.A.T. for Black River Farm Management, LLC\nBlack River Farm Managment, LLC'} />
      <div style={{ fontSize: 12, color: B.textLt, marginTop: -8, marginBottom: 12 }}>
        Alternate spellings the title company might use on a settlement statement. Matching ignores case, punctuation and spacing, so only add a spelling that differs by more than that.
      </div>
      <Textarea label="Notes (optional)" value={form.notes} onChange={v => set('notes', v)} rows={2} />
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
        <Btn variant="primary" onClick={save} loading={saving}>Save</Btn>
      </ModalFooter>
    </Modal>
  );
};

const ENTITY_VIEWS = ['Roster', 'Org Chart', 'Documents'];

export const CompanyEntities = () => {
  const { data, loading, error, reload } = useApi(() => api.get('sage/entities'), []);
  const [editEntity, setEditEntity] = useState(null);
  // A save is applied locally rather than by refetching the whole roster: the
  // PUT already returned the stored row, and useApi has no setter.
  const [saved, setSaved] = useState({});
  const [view, setView] = useState(ENTITY_VIEWS[0]);

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const supportsText = data?.supportsText !== false;
  const entities = (data?.entities || []).map(e => ({ ...e, ...(saved[e.sageEntityID] || {}) }));

  return (
    <div>
      <div style={{ fontSize: 13, color: B.textMd, maxWidth: 760, margin: '4px 0 6px' }}>
        Our legal entities, as imported from Sage. Used to detect whether we are the buyer or the seller on a settlement statement — which sets Purchase vs Sale on the generated journal entry, and merges the buyer and seller copies of one closing into a single JE.
      </div>
      <div style={{ fontSize: 12, color: B.textLt, maxWidth: 760, marginBottom: 14 }}>
        The list comes from the <strong>Source Entities</strong> import on the Sage Import tab — to add or rename one, change it in Sage and re-import. What you set here are the alternate spellings a title company might use.
      </div>

      {!supportsText && (
        <Card style={{ marginBottom: 12, borderColor: B.amber }}>
          <div style={{ fontSize: 13, color: B.text }}>
            Aliases can&apos;t be saved until migration 189 has been applied. The entity list below still works, and matching falls back to the names exactly as Sage has them.
          </div>
        </Card>
      )}

      <Tabs tabs={ENTITY_VIEWS} active={view} onChange={setView} />
      <div style={{ marginTop: 14 }}>
        {view === 'Roster' && (
          <Card style={{ padding: 0 }}>
            {entities.length === 0 ? (
              <div style={{ padding: 24, color: B.textLt, fontSize: 13, textAlign: 'center' }}>
                No entities yet — import the Sage entity list from <strong>Settings → Sage Import → Source Entities</strong>, or email the export to import+ent@harvus.com.
              </div>
            ) : entities.map(e => {
              const aliases = splitAliases(e.aliases);
              // `matchable` is the SERVER's answer (it belongs to the matcher that
              // actually decides Purchase vs Sale, so it is not recomputed here).
              // Saying it out loud beats excluding the entity in silence: an
              // undistinctive name simply never fires, and only an alias fixes it.
              const needsAlias = supportsText && e.matchable === false;
              return (
                <div key={e.sageEntityID} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: `1px solid ${B.border}` }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Badge color={B.navy}>{e.entityCode}</Badge>
                      <span style={{ fontWeight: 600, fontSize: 14, color: B.text }}>{e.name || <span style={{ color: B.textLt, fontWeight: 400 }}>— no name in Sage —</span>}</span>
                      {needsAlias && <Badge color={B.amber}>Needs an alias</Badge>}
                    </div>
                    {aliases.length > 0 && (
                      <div style={{ fontSize: 12, color: B.textLt, marginTop: 2 }}>{aliases.join(' · ')}</div>
                    )}
                    {needsAlias && (
                      <div style={{ fontSize: 12, color: B.amber, marginTop: 2 }}>
                        Too generic to recognise on a settlement statement by itself — add the full legal name as an alias.
                      </div>
                    )}
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <Btn size="sm" variant="secondary" disabled={!supportsText}
                      onClick={() => setEditEntity(e)}>
                      {aliases.length > 0 ? 'Edit aliases' : 'Add aliases'}
                    </Btn>
                  </div>
                </div>
              );
            })}
          </Card>
        )}

        {view === 'Org Chart' && <EntityOrgChart />}
        {view === 'Documents' && (
          <EntityDocumentsPanel entities={entities}
            onEntityChanged={(e) => setSaved(s => ({ ...s, [e.sageEntityID]: { ...(s[e.sageEntityID] || {}), aliases: e.aliases } }))} />
        )}
      </div>

      {editEntity && (
        <AliasModal
          entity={editEntity}
          onClose={() => setEditEntity(null)}
          onSaved={(row) => setSaved(s => ({ ...s, [row.sageEntityID]: row }))}
        />
      )}
    </div>
  );
};
